<template>
    <div
        class="site-card-wrapper"
        @click="goToUrl"
        :class="{ 'is-selected': isSelected }"
    >
        <div class="site-card" :style="dynamicStyles">
            <!-- 图标区域 -->
            <div class="site-icon-box drag-handle">
                <div v-if="site.iconType === 'custom'" class="custom-icon">
                    <img :src="site.iconPath || defaultIcon" @error="onIconError" alt="icon" />
                </div>
                <div v-else class="system-icon">
                    <img :src="defaultIcon" @error="onIconError" alt="icon" />
                </div>
            </div>

            <!-- 文字区域 -->
            <div class="site-info">
                <div class="site-title">{{ site.siteTitle }}</div>
            </div>

            <!-- 操作按钮 (Hover显示) -->
            <div class="site-actions">
                <div class="action-btn edit" @click.stop="showEditModal">
                    <icon-edit />
                </div>
                <div class="action-btn delete" @click.stop="confirmDelete">
                    <icon-delete />
                </div>
            </div>
        </div>

        <!-- 编辑模态框 -->
        <a-modal
            v-model:visible="editModalVisible"
            title="编辑站点"
            :width="360"
            :footer="false"
            class="glass-modal-custom"
            :mask-style="{ backdropFilter: 'blur(8px)', background: 'rgba(0, 0, 0, 0.4)' }"
            :modal-style="{ background: 'transparent', boxShadow: 'none' }"
        >
            <div class="glass-modal-content">
                <a-form :model="editSite" layout="vertical" size="medium">
                    <a-form-item label="标题">
                        <a-input v-model="editSite.siteTitle" placeholder="站点名称" class="glass-input"/>
                    </a-form-item>
                    <a-form-item label="URL">
                        <a-input v-model="editSite.siteUrl" placeholder="https://" class="glass-input"/>
                    </a-form-item>
                    <a-form-item label="图标类型">
                        <a-radio-group v-model="editSite.iconType" type="button" class="glass-radio">
                            <a-radio value="system">自动获取</a-radio>
                            <a-radio value="custom">自定义</a-radio>
                        </a-radio-group>
                    </a-form-item>
                    <a-form-item label="图标地址" v-if="editSite.iconType === 'custom'">
                        <a-space>
                            <a-input v-model="editSite.iconPath" placeholder="图片URL" class="glass-input"/>
                            <a-button @click="openIconLibrary" class="glass-btn-icon">
                                <template #icon><icon-search /></template>
                            </a-button>
                        </a-space>
                    </a-form-item>
                    <a-form-item>
                        <a-button type="primary" long @click="updateSiteData" class="glass-btn-primary">保存修改</a-button>
                    </a-form-item>
                </a-form>
            </div>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, computed, watch, defineProps } from 'vue';
import { useStore } from 'vuex';
import { Message, Modal } from '@arco-design/web-vue';
import { IconEdit, IconDelete, IconSearch } from '@arco-design/web-vue/es/icon';

const props = defineProps({
    site: { type: Object, required: true },
    sectionId: { type: Number, required: true },
    cardStyles: { type: Object, required: true },
});

const store = useStore();
const editModalVisible = ref(false);
const isSelected = ref(false);
const editSite = ref({ ...props.site, sectionId: props.sectionId });

// 默认图标 - 使用本地静态资源，避免外链失效导致的死循环
const defaultFallbackIcon = '/img/error.png';

const getDefaultIcon = () => {
    if (props.site.iconType === 'system' && props.site.siteUrl) {
        // 特殊处理：飞书文档
        if (props.site.siteUrl.includes('feishu.cn')) {
            return 'https://open.feishu.cn/favicon.ico';
        }
        
        // 使用 Google Favicon API
        return `https://www.google.com/s2/favicons?sz=128&domain_url=${encodeURIComponent(props.site.siteUrl)}`;
    }
    return defaultFallbackIcon;
};

const defaultIcon = ref(getDefaultIcon());

// 图片加载失败处理
const onIconError = (e) => {
    // 防止死循环：如果当前图片已经是默认图标，就不再重试
    if (e.target.src.includes('error.png')) return;
    e.target.src = defaultFallbackIcon;
};

watch(() => props.site, (newSite) => {
    editSite.value = { ...newSite, sectionId: props.sectionId };
    defaultIcon.value = getDefaultIcon();
}, { deep: true });

// 动态样式
const dynamicStyles = computed(() => {
    const { opacity, backgroundColor, borderColor, boxShadow } = props.cardStyles;
    
    // 基础样式 - 增加默认透明度，减少高亮
    let style = {
        backgroundColor: backgroundColor || 'rgba(255, 255, 255, 0.6)', 
        border: `1px solid ${borderColor || 'rgba(255,255,255,0.4)'}`,
        opacity: opacity || 1,
    };
    
    // 阴影处理
    if (boxShadow) {
        const shadowStr = `${boxShadow.horizontal} ${boxShadow.vertical} ${boxShadow.blur} ${boxShadow.spread} ${boxShadow.color}${boxShadow.inset ? ' inset' : ''}`;
        style.boxShadow = shadowStr;
    }

    if (isSelected.value) {
        style.borderColor = '#165DFF';
        style.boxShadow = '0 0 0 2px rgba(22, 93, 255, 0.2)';
    }

    return style;
});

// Actions
const updateSite = (data) => store.dispatch('sites/updateSite', data);
const deleteSite = (data) => store.dispatch('sites/deleteSite', data);
const fetchOrganizations = () => store.dispatch('organizations/fetchOrganizations');

const openIconLibrary = () => window.open('https://icon-sets.iconify.design/', '_blank');

const goToUrl = (event) => {
    if (event.target.closest('.site-actions')) return;
    if (props.site.siteUrl) {
        window.open(props.site.siteUrl, '_blank');
    }
};

const showEditModal = () => { editModalVisible.value = true; };

const updateSiteData = async () => {
    if (!editSite.value.siteTitle || !editSite.value.siteUrl) {
        Message.warning('请填写完整的站点信息');
        return;
    }
    if(editSite.value.iconType === 'system') {
        try {
             if (editSite.value.siteUrl.includes('feishu.cn')) {
                 editSite.value.iconPath = 'https://open.feishu.cn/favicon.ico';
             } else {
                 editSite.value.iconPath = `https://www.google.com/s2/favicons?sz=128&domain_url=${encodeURIComponent(editSite.value.siteUrl)}`;
             }
        } catch(e) {}
    }

    try {
        await updateSite(editSite.value);
        await fetchOrganizations();
        Message.success('更新成功');
        editModalVisible.value = false;
    } catch (error) {
        Message.error('更新失败');
    }
};

const confirmDelete = () => {
    Modal.warning({
        title: '确认删除',
        content: '确定要删除这个站点吗？',
        hideCancel: false,
        onOk: async () => {
            try {
                await deleteSite({ siteId: props.site.siteId, sectionId: props.sectionId });
                await fetchOrganizations();
                Message.success('已删除');
            } catch (error) {
                Message.error('删除失败');
            }
        }
    });
};
</script>

<style scoped>
.site-card-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
}

.site-card {
    display: flex;
    align-items: center;
    padding: 10px 16px; /* 减小内边距 */
    border-radius: 14px; /* 圆角稍微减小一点以匹配高度 */
    cursor: pointer;
    position: relative;
    height: 60px; /* 高度从 72px 降低到 60px */
    /* 移除模糊，增加通透感 */
    background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 100%);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-top: 1px solid rgba(255, 255, 255, 0.7);
    border-left: 1px solid rgba(255, 255, 255, 0.7);
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    overflow: hidden;
}

/* Hover 状态 */
.site-card-wrapper:hover .site-card {
    transform: translateY(-2px); 
    background: linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 100%) !important;
    border-color: rgba(255,255,255,0.8);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    z-index: 10;
}

.site-icon-box {
    width: 36px; /* 图标尺寸适配新高度 */
    height: 36px;
    margin-right: 12px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 10px;
    overflow: hidden;
    transition: transform 0.3s ease;
    z-index: 1;
}

.site-card-wrapper:hover .site-icon-box {
    transform: scale(1.05); /* 轻微放大 */
}

.site-icon-box img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.site-info {
    flex: 1;
    min-width: 0; 
    display: flex;
    flex-direction: column;
    justify-content: center;
    z-index: 1;
}

.site-title {
    font-size: 0.95rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.8); /* 字体颜色稍微加深 */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
}

.site-actions {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    gap: 6px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    z-index: 2;
}

.site-card-wrapper:hover .site-actions {
    opacity: 1;
    pointer-events: auto;
}

.action-btn {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    color: #4E5969;
    transition: all 0.2s;
}

.action-btn:hover {
    color: #165DFF;
    background: #fff;
    transform: scale(1.05);
}

.action-btn.delete:hover {
    color: #F53F3F;
}

/* 玻璃模态框样式 */
.glass-modal-content {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(20px) saturate(180%);
    padding: 24px;
    border-radius: 16px;
    border: 1px solid rgba(255,255,255,0.5);
    box-shadow: 0 20px 50px rgba(0,0,0,0.1);
}

/* 覆盖 Arco 默认样式 */
:deep(.glass-input .arco-input-wrapper),
:deep(.glass-input .arco-select-view) {
    background: rgba(255, 255, 255, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(4px);
}

:deep(.glass-btn-primary) {
    background: linear-gradient(135deg, #165DFF 0%, #0099FF 100%);
    border: none;
    box-shadow: 0 4px 12px rgba(22, 93, 255, 0.3);
}

@media (max-width: 768px) {
    .site-card {
        padding: 10px;
        height: 64px;
    }
    .site-icon-box {
        width: 36px;
        height: 36px;
        margin-right: 12px;
    }
    .site-title {
        font-size: 0.9rem;
    }
    .site-actions {
        display: none; 
    }
}
</style>
